import { AfterViewInit, Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import {
  ProjectType,
  KazaService as SDKKazaService,
  AuthenticationService as SDKAuthenticationService,
  User,
  Kaza,
  ProjectTypeService as SDKProjectTypes,
} from '@adeo/ngx-kozikaza-api';
import { CountryLegacyService as SDKCountryLegacy, CountryLegacy } from '@adeo/ngx-kozikaza-api/misc/country-legacy';
import { UntypedFormArray, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { combineLatest, Observable, Subscription, throwError } from 'rxjs';
import { catchError, filter, finalize, map, share, switchMap, tap } from 'rxjs/operators';
import { AuthPopinService } from '../auth-popin.service';
import { AuthService } from '../auth.service';
import { minCheckboxSelected } from '../../../utils/validators/min-checkbox-selected.validator';
import { ToastrService } from '../../../shared/services/utilities/toastr.service';
import { FormCheckboxService } from '../../../shared/services/utilities/form-checkbox.service';
import { UserStoreService } from '../../../shared/services/state-management/user-store.service';
import { fadeInAnimation } from '../../../shared/animations/fade-in.animation';
import { KazaService as LegacyKazaService } from '../../../shared/services/features/kaza/kaza.service';
import { RoutesService } from "../../../shared/services/routes/routes.service";
import { KzIconsRegistryService } from "../../../kz-ui/ui/icon/kz-icons-registry.service";
import { kzIconsInfos } from "../../../kz-ui/ui/icon/generated-icons/kzIcons-infos.icon";

@Component({
  selector: 'app-kaza',
  templateUrl: './kaza.component.html',
  styleUrls: ['./kaza.component.scss'],
  animations: [fadeInAnimation]
})
export class KazaComponent implements OnInit, OnDestroy, AfterViewInit {
  kazaForm: UntypedFormGroup;
  projectTypes: ProjectType[];
  countries: CountryLegacy[];
  currentUser: User;
  kaza: any;
  countryFormSubscription: Subscription;
  kazaPutSubscription: Subscription;
  subscription: Subscription;
  formArray: UntypedFormArray;
  indexNoWork: number;
  projectTypeNoWork: string;
  submitted = false;
  hasError = false;
  loadingProjectTypes = true;
  isInKazaplan$: Observable<boolean> = this.routesService.routeDatas.pipe(
    map((data) => !!data.isKazaplanRoute),
    share(),
  );

  constructor(
      private router: Router,
      private activatedRoute: ActivatedRoute,
      private fb: UntypedFormBuilder,
      private formCheckboxService: FormCheckboxService,
      private sdkKazaService: SDKKazaService,
      private legacyKazaService: LegacyKazaService,
      private sdkAuthenticationService: SDKAuthenticationService,
      private toastr: ToastrService,
      private authPopinService: AuthPopinService,
      private authService: AuthService,
      public readonly userStoreService: UserStoreService,
      private readonly sdkProjectTypesService: SDKProjectTypes,
      private readonly sdkCountryLegacyService: SDKCountryLegacy,
      private readonly routesService: RoutesService,
      private readonly kzIconsRegistryService: KzIconsRegistryService,
  ) {
    this.kazaForm = this.fb.group({
      types: ['', [Validators.required]],
      country: [((this.kaza && this.kaza.country) ? this.kaza.country : 'FR'), [Validators.required]],
      postalCode: [((this.kaza && this.kaza.postalCode) ? this.kaza.postalCode : ''), [Validators.required]]
    });
    this.formArray = new UntypedFormArray([], minCheckboxSelected(1));
    this.kzIconsRegistryService.registerIcons([kzIconsInfos]);
  }

  ngOnInit() {
    this.subscription = combineLatest([
      this.loadKaza(),
      this.loadProjectTypes(),
      this.loadCountries()
    ]).pipe(
        tap(([kaza, projectTypes, countries]) => {
          this.kazaForm.patchValue({
            postalCode: this.kaza.postalCode,
            country: ((this.kaza && this.kaza.country) ? this.kaza.country : 'FR')
          });
          if (projectTypes) {
            this.formCheckboxService.buildCheckbox(this.formArray, projectTypes, kaza.types);
          }
        })
    ).subscribe();
  }

  ngAfterViewInit(): void {
    this.countryOnChanges();
  }

  private loadKaza(): Observable<Kaza> {
    return this.userStoreService.userStore.pipe(
        filter((user) => !!user),
        switchMap((user) => {
          this.currentUser = user;
          return this.sdkKazaService.getKaza(this.currentUser.activeKaza, ['types']).pipe(
              catchError((e) => {
                this.toastr.error($localize`:@@toast.error_try_again:An error has occurred. Please try again`);
                return throwError(e);
              }),
              tap((kaza) => this.kaza = kaza)
          );
        })
    );
  }

  private loadProjectTypes(): Observable<Array<ProjectType>> {
    return this.sdkProjectTypesService.getProjectTypes(null).pipe(
        catchError((e) => {
          this.toastr.error($localize`:@@toast.error_try_again:An error has occurred. Please try again`);
          return throwError(e);
        }),
        finalize(() => this.loadingProjectTypes = false),
        map((data) => {
          this.projectTypes = data['hydra:member'];
          this.indexNoWork = this.projectTypes.findIndex((i: ProjectType) => i.type === 'no-work');
          this.projectTypeNoWork = this.projectTypes[this.indexNoWork]['@id'];
          this.formCheckboxService.buildCheckbox(this.formArray, this.projectTypes, (this.kaza && this.kaza.types) ? this.kaza.types : []);
          this.kazaForm.setControl('types', this.formArray);
          return this.projectTypes;
        })
    );
  }

  loadCountries(): Observable<Array<CountryLegacy>> {
    return this.sdkCountryLegacyService.getCountries().pipe(
        tap((data) => {
          this.countries = data;
        })
    );
  }

  isFieldInvalid(name: string): boolean {
    if (!this.kazaForm) {
      return false;
    }

    const control = this.kazaForm.get(name);
    if (!control) {
      return false;
    }
    return (this.hasError || control.dirty || control.touched) && control.invalid;
  }

  save(): void {
    if (this.kazaForm.valid) {
      this.hasError = false;
      this.submitted = true;

      this.kazaPutSubscription = this.legacyKazaService.update(this.kaza['@id'], {
        country: this.kazaForm.get('country').value,
        postalCode: this.kazaForm.get('postalCode').value ? this.kazaForm.get('postalCode').value.toString() : '',
        types: this.formCheckboxService.formatData(this.kazaForm.get('types').value, this.projectTypes)
      }).pipe(
          finalize(() => this.submitted = false),
          catchError((e) => {
            this.toastr.error($localize`:@@toast.error_try_again:An error has occurred. Please try again`);
            return throwError(e);
          }),
          tap((kazaUpdated: Kaza) => this.next(kazaUpdated))
      ).subscribe();
    } else {
      this.hasError = true;
    }
  }

  next(kaza: Kaza): void {
    let types = kaza.types;
    types = types.map((type) => {
      const typeMapped = this.projectTypes.find((projectType) => projectType['@id'] === ((typeof type === 'string') ? type : type['@id']));
      return typeMapped.type;
    });

    if (types.includes('no-work')) {
      this.close();
    } else {
      this.authService.redirectToConstructionDateStep();
    }
  }

  prev(): void {
    this.authService.redirectToProfileStep();
  }

  close(): void {
    this.authPopinService.closePopinAndRedirect();
  }

  changeProjectTypes(value: ProjectType): void {
    if (value.type === 'no-work') {
      // Check only kazaTypeNoWork
      this.formCheckboxService.buildCheckbox(this.formArray, this.projectTypes, [this.projectTypes[this.indexNoWork]]);
    } else {
      // If other value, set false to checkbox of kazaTypeNoWork
      const fa = this.kazaForm.get('types') as UntypedFormArray;
      fa.at(this.indexNoWork).setValue(false, { emitEvent: false});
    }
  }

  countryOnChanges(): void {
    this.countryFormSubscription = this.kazaForm.get('country').valueChanges.subscribe(
        data => {
          if (data !== 'FR') {
            this.kazaForm.get('postalCode').setValidators(null);
          } else {
            this.kazaForm.get('postalCode').setValidators([Validators.required, this.postalCodeValidator]);
          }

          this.kazaForm.get('postalCode').updateValueAndValidity();
        });
  }

  postalCodeValidator(c: UntypedFormControl) {
    const validPostalCode = /^[0-9]{5}$/i;
    const group = c.parent;
    let country: string;

    if (group) {
      country = group.get('country').value;

      if (('FR' !== country && '' !== c.value) || ('FR' === country && validPostalCode.test(c.value))) {
        return null;
      }
    }

    return {postalCodeValidError: {value: true}};
  }

  ngOnDestroy(): void {
    if (this.countryFormSubscription && !this.countryFormSubscription.closed) {
      this.countryFormSubscription.unsubscribe();
    }
    if (this.kazaPutSubscription && !this.kazaPutSubscription.closed) {
      this.kazaPutSubscription.unsubscribe();
    }
    if (this.subscription && !this.subscription.closed) {
      this.subscription.unsubscribe();
    }
  }
}
